import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { auth } from "../db/firebase";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { useTranslation } from "react-i18next";

const inputBase =
  "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all";
const labelClass =
  "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get("oobCode");
  const mode = searchParams.get("mode");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeValid, setCodeValid] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    if (mode !== "resetPassword" || !oobCode) {
      setCodeValid(false);
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        setCodeValid(true);
        setUserEmail(email);
      })
      .catch(() => setCodeValid(false));
  }, [mode, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t("passwordsDoNotMatch"));
      return;
    }
    if (password.length < 6) {
      setError(t("passwordTooShort") || "Password must be at least 6 characters.");
      return;
    }
    if (!oobCode) return;
    setIsSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(
        err.code === "auth/expired-action-code"
          ? (t("resetLinkExpired") || "This reset link has expired. Please request a new one.")
          : err.code === "auth/invalid-action-code"
            ? (t("resetLinkInvalid") || "This reset link is invalid or has already been used.")
            : err.message || (t("error") || "An error occurred")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (codeValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-[#5932EA]/5 dark:from-gray-900 dark:via-gray-900 dark:to-[#5932EA]/10 p-4">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#5932EA] border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (codeValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-[#5932EA]/5 dark:from-gray-900 dark:via-gray-900 dark:to-[#5932EA]/10 p-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              {t("resetLinkExpired") || "This reset link has expired. Please request a new one."}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t("resetLinkExpiredDescription") || "Password reset links expire after 1 hour. Please go back to the login page and request a new password reset."}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#5932EA] text-white font-semibold rounded-xl hover:bg-[#4a28c7] transition-colors"
            >
              {t("backToLogin") || "Back to login"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-[#5932EA]/5 dark:from-gray-900 dark:via-gray-900 dark:to-[#5932EA]/10 p-4">
        <div className="w-full max-w-md">
          <div className="rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              {t("passwordResetSuccess") || "Password reset successfully!"}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t("redirectingToLogin") || "Redirecting you to login..."}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#5932EA] text-white font-semibold rounded-xl hover:bg-[#4a28c7] transition-colors"
            >
              {t("login")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-[#5932EA]/5 dark:from-gray-900 dark:via-gray-900 dark:to-[#5932EA]/10 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md">
        <div className="rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-6 py-8 sm:px-8 sm:py-10 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-white dark:from-gray-800 dark:to-gray-800/80">
            <div className="flex items-center gap-3">
              <img
                src="/imgs/logo-192x192.png"
                alt="Praisify"
                width={64}
                height={64}
                className="w-16 h-16 rounded-2xl shadow-sm shrink-0"
              />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 tracking-tight">
                  {t("resetPassword") || "Reset password"}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {userEmail ? `${t("for") || "for"} ${userEmail}` : t("enterNewPassword") || "Enter your new password"}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            <div className="space-y-5">
              <div>
                <label htmlFor="password" className={labelClass}>
                  {t("password")}
                </label>
                <input
                  maxLength={100}
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("password")}
                  required
                  minLength={6}
                  className={inputBase}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelClass}>
                  {t("confirmPassword")}
                </label>
                <input
                  maxLength={100}
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t("confirmPassword")}
                  required
                  minLength={6}
                  className={inputBase}
                />
              </div>
            </div>

            {error && (
              <p className="mt-4 text-sm text-red-500 dark:text-red-400 text-center bg-red-50 dark:bg-red-900/20 py-2 px-3 rounded-xl">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 py-3 bg-[#5932EA] text-white font-semibold rounded-xl hover:bg-[#4a28c7] disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isSubmitting ? t("loading") || "Loading..." : t("resetPassword") || "Reset password"}
            </button>
          </form>

          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              <Link
                to="/login"
                className="text-[#5932EA] hover:text-[#4a28c7] font-medium transition-colors"
              >
                {t("backToLogin") || "Back to login"}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
